import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { CtaBand } from '../components/ui/CtaBand';
import { Eyebrow } from '../components/ui/Eyebrow';
import { SectionHeading } from '../components/ui/SectionHeading';
import { TreatmentCard } from '../components/treatments/TreatmentCard';
import { treatments, getTreatment } from '../data/treatments';
import { getCarePath } from '../data/carePaths';
import { useBrand } from '../contexts/BrandContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
export function TreatmentDetail() {
  const {
    slug = ''
  } = useParams();
  const treatment = getTreatment(slug);
  const {
    brand,
    treatmentImage
  } = useBrand();
  useDocumentMeta({
    title: treatment ? `${treatment.name} — ${brand.name}` : `Not found — ${brand.name}`,
    description: treatment?.tagline ?? 'This treatment could not be found.'
  });
  if (!treatment) {
    return <main className="w-full bg-cream" data-id="element-774" data-mp="smttk2clfwms">
        <div className="mx-auto max-w-shell px-6 py-32 md:px-8" data-id="element-775" data-mp="snlmt6ipxi9w">
          <h1 className="font-serif text-4xl text-ink" data-id="element-776" data-mp="nnz6upbi9nxe">We couldn’t find that treatment.</h1>
          <p className="mt-5 max-w-lg leading-relaxed text-ink-soft" data-id="element-777" data-mp="11n8ev1pk5bqa">
            That link may be out of date — our fault, not yours.
          </p>
          <Button to="/treatments" className="mt-8" data-id="element-778" data-mp="1bkmvb1bhe0il">
            See all treatments
          </Button>
        </div>
      </main>;
  }
  const carePath = getCarePath(treatment.carePath);
  const related = treatments.filter(item => item.slug !== treatment.slug).slice(0, 3);
  return <main className="w-full bg-cream" data-id="element-779" data-mp="1lc928lgsddfj">
      <div className="mx-auto max-w-shell px-6 pt-10 md:px-8 md:pt-14" data-id="element-780" data-mp="1gb36sfvqrc2b">
        <Link to="/treatments" className="inline-flex items-center gap-2 text-sm text-ink-soft transition-colors duration-150 ease-brand hover:text-brick" data-id="element-781" data-mp="1ub4ae33e4xlh">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-782" data-mp="1pa6q9kytlthc" />
          All treatments
        </Link>
      </div>

      <header className="bg-cream" data-id="element-783" data-mp="rxggqhsdhufh">
        <div className="mx-auto grid max-w-shell gap-12 px-6 py-12 md:px-8 md:py-16 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20" data-id="element-784" data-mp="ryrg85escg0s">
          <img src={treatmentImage(treatment.slug, treatment.image)} alt="" className="h-72 w-full rounded-brand object-cover md:h-[26rem]" data-id="element-785" data-mp="vy0e9mguz025" />
          <div data-id="element-786" data-mp="15ol7svhodee7">
            <Eyebrow className="text-brick" data-id="element-787" data-mp="15r7fcv4kftwf">
              {treatment.category} · {treatment.status}
            </Eyebrow>
            <h1 className="mt-5 font-serif text-[2.5rem] leading-[1.04] text-ink md:text-[3.5rem]" data-id="element-788" data-mp="1fnaqurlb54w1">
              {treatment.name}
            </h1>
            <p className="mt-5 max-w-xl font-serif text-2xl italic leading-snug text-brick" data-id="element-789" data-mp="1jnkye8or03d0">
              {treatment.tagline}
            </p>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-soft" data-id="element-790" data-mp="3hh07hc2fwqwp">
              {treatment.description}
            </p>

            <div className="rule mt-8 pt-6" data-id="element-791" data-mp="3dwzs8osvys3q">
              <p className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-792" data-mp="3dyt20cyspsfy">
                What the evidence says
              </p>
              <p className="mt-3 max-w-xl leading-relaxed text-ink" data-id="element-793" data-mp="346ez3gvzefs7">{treatment.evidence}</p>
            </div>

            <div className="rule mt-8 flex flex-wrap items-baseline gap-x-6 gap-y-2 pt-6" data-id="element-794" data-mp="3064izbeh68zm">
              <p className="font-serif text-4xl text-ink" data-id="element-795" data-mp="qg8b29ovnum">
                ${treatment.price}
                <span className="text-base text-ink-muted" data-id="element-796" data-mp="3rvzks7jirr56"> {treatment.cadence}</span>
              </p>
              <p className="text-ink-soft" data-id="element-797" data-mp="3nu4ip0trl9qc">{treatment.priceNote}</p>
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-4" data-id="element-798" data-mp="3iw2xoacs30c8">
              <Button to="/start" data-id="element-799" data-mp="3ixxdja97029g">
                Start with one question
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-800" data-mp="2chbrps8sspbc" />
              </Button>
              {carePath && <Button to={`/care/${carePath.slug}`} variant="secondary" data-id="element-801" data-mp="27j0klp9w0swt">
                  See the {carePath.name} path
                </Button>}
            </div>
          </div>
        </div>
      </header>

      <section className="bg-cream-light" data-id="element-802" data-mp="3mcxitw24tqwa">
        <div className="mx-auto grid max-w-shell gap-14 px-6 py-20 md:px-8 md:py-24 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20" data-id="element-803" data-mp="3me8l1rfxiqla">
          <div data-id="element-804" data-mp="3qc6bsypclkqs">
            <Eyebrow className="text-brick" data-id="element-805" data-mp="hyfthr6bp8pj">Worth knowing</Eyebrow>
            <ul className="mt-8" data-id="element-806" data-mp="hz8ro3r7upw2">
              {treatment.highlights.map(item => <li key={item} className="rule flex items-start gap-4 py-4" data-id="element-807" data-mp="mx0ieug4k5yo">
                  <CheckIcon className="mt-1.5 h-4 w-4 shrink-0 text-brick" strokeWidth={2} aria-hidden="true" data-id="element-808" data-mp="8wpqrp6zqncg" />
                  <span className="leading-relaxed text-ink" data-id="element-809" data-mp="2tnqjiuuusx9z">{item}</span>
                </li>)}
            </ul>
          </div>

          <div data-id="element-810" data-mp="2yoebbiqwpi59">
            <Eyebrow className="text-brick" data-id="element-811" data-mp="2ykqfgcxzojnd">How it is dispensed</Eyebrow>
            <dl className="mt-8" data-id="element-812" data-mp="2q4ho6l5jb7rg">
              {treatment.facts.map(fact => <div key={fact.label} className="rule flex justify-between gap-6 py-4" data-id="element-813" data-mp="2m3pczmd3qssj">
                  <dt className="text-ink-soft" data-id="element-814" data-mp="30376ug598w8r">{fact.label}</dt>
                  <dd className="text-right text-ink" data-id="element-815" data-mp="2v55m7qs6kov6">{fact.value}</dd>
                </div>)}
              <div className="rule flex justify-between gap-6 py-4" data-id="element-816" data-mp="27fbbpfvc3ce3">
                <dt className="text-ink-soft" data-id="element-817" data-mp="27di1xrpfcc1v">Dispensed by</dt>
                <dd className="text-right text-ink" data-id="element-818" data-mp="22ht17l1o4p3z">{treatment.pharmacy}</dd>
              </div>
              <div className="rule flex justify-between gap-6 py-4" data-id="element-819" data-mp="2h6foqa1g8n6a">
                <dt className="text-ink-soft" data-id="element-820" data-mp="2ce5u4gpe2yxo">Strength</dt>
                <dd className="text-right text-ink" data-id="element-821" data-mp="2cckz6pz5xdza">{treatment.strength}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="bg-cream" data-id="element-822" data-mp="1jqs472k3z3n2">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-823" data-mp="1jpqje6daj2s5">
          <SectionHeading eyebrow="Other options" title="Compare before you decide." data-id="element-824" data-mp="3jrgkf2sjzr97" />
          <ul className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-825" data-mp="3oqb1o0avh7r7">
            {related.map(item => <li key={item.slug} className="flex" data-id="element-826" data-mp="3tjci4cqq9fcw">
                <TreatmentCard treatment={item} data-id="element-827" data-mp="1e8wtey6v3ci" />
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-828" data-mp="30u5nvpyb3bxg" />
    </main>;
}